/**
 * AIResultsHeader.jsx
 *
 * Browse page sub-component: renders the personalized AI-curated gifts banner
 * (personality summary) + the refinement toolbar (More like this / Show
 * alternatives / Cheaper options / Clear / Save-search). Extracted from
 * Browse.jsx (Feb 2026) to reduce that file's complexity.
 */
import { toast } from 'sonner';
import { api } from '@/App';
import { useState } from 'react';

import { logger } from '@/utils/logger';
import { Save, Share2, Copy, Check, ThumbsUp, BadgeDollarSign, X, Loader2 } from 'lucide-react';
export function AIResultsHeader({
  aiContext,
  loadingMoreLikeThis,
  handleMoreLikeThis,
  filteredProducts,
  setFilteredProducts,
  setShowAIResults,
  setAiRecommendationsMap,
  setAiContext,
  applyFilters,
  user,
  darkMode,
}) {
  const toolbarButtonStyle = {
    background: darkMode.isDark ? 'rgba(254, 215, 165, 0.2)' : 'white',
    color: darkMode.textPrimary,
    border: `1px solid ${darkMode.isDark ? '#FED7A5' : '#E5E7EB'}`,
  };

  const handleCheaperOptions = () => {
    const prices = filteredProducts
      .map((p) => parseFloat(p.price))
      .filter((p) => !isNaN(p));
    if (prices.length === 0) {
      toast.error('Cannot filter by price');
      return;
    }
    prices.sort((a, b) => a - b);
    const medianPrice = prices[Math.floor(prices.length / 2)];
    const cheaper = filteredProducts.filter(
      (p) => parseFloat(p.price) <= medianPrice
    );
    if (cheaper.length === 0) {
      toast.error('No cheaper options available');
      return;
    }
    setFilteredProducts(cheaper);
    toast.success(`Showing ${cheaper.length} more affordable options`);
  };

  const handleClearAIResults = () => {
    setShowAIResults(false);
    setAiRecommendationsMap(new Map());
    setAiContext({ personalitySummary: '', recipientName: '' });
    // Restore the regular catalog grid (respecting any active filters)
    // instead of leaving the page empty.
    applyFilters();
    toast.info('Cleared AI recommendations');
  };

  const handleSaveSearch = async () => {
    try {
      await api.post('/saved-searches', {
        name: aiContext.recipientName ? `Gifts for ${aiContext.recipientName}` : 'My gift search',
        recipient_id: null,
        criteria: { aiContext: aiContext.personalitySummary },
        notification_preference: 'both',
      });
      toast.success('✅ Search saved! Check "My Alerts" to manage it.');
    } catch (error) {
      logger.error('Failed to save search:', error);
      toast.error('Failed to save search');
    }
  };

  const [shareState, setShareState] = useState({ open: false, url: '', copied: false, loading: false });

  const handleShare = async () => {
    if (filteredProducts.length === 0) {
      toast.error('Nothing to share yet');
      return;
    }
    setShareState((s) => ({ ...s, loading: true }));
    try {
      const productIds = filteredProducts
        .map((p) => p.id)
        .filter(Boolean)
        .slice(0, 60);
      const res = await api.post('/shared-shortlists', {
        recipient_name: aiContext.recipientName || '',
        personality_summary: aiContext.personalitySummary || '',
        product_ids: productIds,
      });
      const shareUrl = `${window.location.origin}${res.data.share_url}`;
      // Try native share on mobile / clipboard everywhere else.
      let copied = false;
      try {
        if (navigator.share && /Mobi|Android|iPhone/i.test(navigator.userAgent)) {
          await navigator.share({
            title: 'Gift ideas from Mementa',
            text: aiContext.recipientName
              ? `I curated a gift shortlist for ${aiContext.recipientName}, take a look:`
              : 'I curated a gift shortlist for you, take a look:',
            url: shareUrl,
          });
        } else {
          await navigator.clipboard.writeText(shareUrl);
          copied = true;
        }
      } catch (_) {
        // User dismissed native share sheet, or clipboard denied, fall
        // through to modal so they can copy manually.
      }
      setShareState({ open: true, url: shareUrl, copied, loading: false });
      if (copied) toast.success('Link copied, paste it anywhere');
    } catch (err) {
      logger.error('Failed to create shareable link:', err);
      toast.error('Could not create share link, try again');
      setShareState((s) => ({ ...s, loading: false }));
    }
  };

  const handleCopyAgain = async () => {
    try {
      await navigator.clipboard.writeText(shareState.url);
      setShareState((s) => ({ ...s, copied: true }));
      toast.success('Link copied');
    } catch (_) {
      toast.error('Copy failed, select and copy manually');
    }
  };

  return (
    <>
      {/* Personalized banner */}
      {aiContext.recipientName && (
        <div
          data-testid="ai-curated-banner"
          className="mb-6 p-6 rounded-xl transition-colors"
          style={{
            background: darkMode.isDark ? 'rgba(254, 215, 165, 0.1)' : '#FFF8F0',
            border: `2px solid ${darkMode.isDark ? 'rgba(254, 215, 165, 0.3)' : '#FED7A5'}`,
          }}
        >
          <h3
            className="text-lg font-semibold mb-2 transition-colors"
            style={{ color: darkMode.textPrimary }}
          >
            ✨ AI-Curated Gifts{' '}
            {aiContext.recipientName !== 'you'
              ? `for ${aiContext.recipientName}`
              : 'for You'}
          </h3>
          {aiContext.personalitySummary && (
            <p
              className="text-sm transition-colors"
              style={{ color: darkMode.textSecondary }}
            >
              {aiContext.personalitySummary}
            </p>
          )}
        </div>
      )}

      {/* Refinement toolbar — one compact row */}
      <div
        data-testid="ai-refinement-toolbar"
        className="mb-6 px-3 py-2 rounded-xl transition-colors flex flex-wrap items-center gap-2"
        style={{
          background: darkMode.isDark ? 'rgba(254, 215, 165, 0.08)' : '#FFF8F0',
          border: `1px solid ${darkMode.isDark ? 'rgba(254, 215, 165, 0.25)' : '#FED7A5'}`,
        }}
      >
        <button
          data-testid="refine-more-like-this-btn"
          onClick={handleMoreLikeThis}
          disabled={loadingMoreLikeThis}
          className="px-3 py-1.5 rounded-full text-sm font-medium transition-all hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1.5"
          style={toolbarButtonStyle}
        >
          {loadingMoreLikeThis ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <ThumbsUp className="w-4 h-4" />
          )}
          {loadingMoreLikeThis ? 'Finding…' : 'More like this'}
        </button>

        <button
          data-testid="refine-cheaper-options-btn"
          onClick={handleCheaperOptions}
          className="px-3 py-1.5 rounded-full text-sm font-medium transition-all hover:scale-105 flex items-center gap-1.5"
          style={toolbarButtonStyle}
        >
          <BadgeDollarSign className="w-4 h-4" />
          Cheaper
        </button>

        <div className="ml-auto flex items-center gap-1.5">
          {user && (
            <button
              data-testid="refine-save-search-btn"
              onClick={handleSaveSearch}
              className="p-2 rounded-full transition-all hover:scale-110"
              title="Save this search"
              style={{ background: '#9E6752', color: 'white' }}
            >
              <Save className="w-4 h-4" />
            </button>
          )}
          <button
            data-testid="refine-share-btn"
            onClick={handleShare}
            disabled={shareState.loading}
            className="p-2 rounded-full transition-all hover:scale-110 disabled:opacity-60"
            title="Share these picks"
            style={{ background: '#2D4354', color: '#FED7A5' }}
          >
            {shareState.loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Share2 className="w-4 h-4" />}
          </button>
          <button
            data-testid="refine-clear-btn"
            onClick={handleClearAIResults}
            className="px-3 py-1.5 rounded-full text-sm font-semibold transition-all hover:scale-105 flex items-center gap-1"
            title="Clear results and browse everything"
            style={{ background: '#DC2626', color: 'white' }}
          >
            <X className="w-4 h-4" />
            Clear
          </button>
        </div>
      </div>

      {shareState.open && (
        <div
          data-testid="share-shortlist-modal"
          className="fixed inset-0 z-[60] flex items-center justify-center p-4"
          style={{ background: 'rgba(0,0,0,0.5)' }}
          onClick={() => setShareState((s) => ({ ...s, open: false }))}
        >
          <div
            className="w-full max-w-md rounded-2xl p-6 shadow-2xl"
            style={{ background: darkMode.isDark ? '#2D4354' : 'white' }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center gap-3 mb-3">
              <div
                className="w-10 h-10 rounded-full flex items-center justify-center"
                style={{ background: '#FED7A5' }}
              >
                <Share2 className="w-5 h-5" style={{ color: '#9E6752' }} />
              </div>
              <div>
                <h3
                  className="text-lg font-semibold"
                  style={{ color: darkMode.textPrimary, fontFamily: 'Fraunces, serif' }}
                >
                  Shortlist ready to share
                </h3>
                <p className="text-xs" style={{ color: darkMode.textSecondary }}>
                  Link expires in 7 days
                </p>
              </div>
            </div>
            <p className="text-sm mb-4" style={{ color: darkMode.textSecondary }}>
              Send this to a partner or group chat. Anyone with the link can
              view the picks and tap the ones they love.
            </p>
            <div
              className="flex items-center gap-2 rounded-lg px-3 py-2 mb-4"
              style={{
                background: darkMode.isDark ? 'rgba(0,0,0,0.25)' : '#F8F4F1',
                border: `1px solid ${darkMode.isDark ? 'rgba(254,215,165,0.2)' : '#E5D5C5'}`,
              }}
            >
              <input
                readOnly
                value={shareState.url}
                data-testid="share-shortlist-url"
                className="flex-1 bg-transparent text-sm outline-none min-w-0"
                style={{ color: darkMode.textPrimary }}
                onFocus={(e) => e.target.select()}
              />
              <button
                onClick={handleCopyAgain}
                data-testid="share-shortlist-copy-btn"
                className="flex-shrink-0 flex items-center gap-1 px-3 py-1.5 rounded-md text-xs font-semibold transition-transform hover:scale-105"
                style={{ background: '#9E6752', color: 'white' }}
              >
                {shareState.copied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
                {shareState.copied ? 'Copied' : 'Copy'}
              </button>
            </div>
            <button
              onClick={() => setShareState((s) => ({ ...s, open: false }))}
              data-testid="share-shortlist-close-btn"
              className="w-full py-2 rounded-lg text-sm font-medium transition-colors"
              style={{
                background: darkMode.isDark ? 'rgba(254,215,165,0.15)' : '#FFF8F0',
                color: darkMode.textPrimary,
                border: `1px solid ${darkMode.isDark ? 'rgba(254,215,165,0.3)' : '#FED7A5'}`,
              }}
            >
              Done
            </button>
          </div>
        </div>
      )}
    </>
  );
}
